"use client";

import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import AnimationContainer from "../utils/animation-container";
import { useState } from "react";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import { AnimatedCircularProgress, OtpInput } from "../index"
import { WrapBg } from '../utils/WrapBg';

const VerifyPhoneSection = () => {
    const router = useRouter();
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    const normalizePhoneNumber = (input: string) => {
        let phoneNumber = input.trim();
        if (phoneNumber.startsWith("0")) phoneNumber = "+98" + phoneNumber.slice(1);
        if (!phoneNumber.startsWith("+98")) phoneNumber = "+98" + phoneNumber;
        return phoneNumber;
    };

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const formattedPhone = normalizePhoneNumber(phone);
        setPhone(formattedPhone);

        const res = await fetch("/api/auth/send-verification-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier: formattedPhone, method: 'phone' }),
        });

        const data = await res.json();
        setLoading(false);

        if (res.ok) {
            toast.custom(
                (t: any) => (
                    <div className="flex items-center gap-3 bg-purple-800/10 rounded-lg p-4 border-[1px]  border-purple-900/30">
                        <AnimatedCircularProgress
                            duration={120}
                            onComplete={() => {
                                setStep(1);
                                toast.error("⏳ مهلت تمام شد!", {
                                    description: "لطفاً دوباره درخواست کد بدهید.",
                                    position: "top-right",
                                });
                                toast.dismiss(t.id);
                            }}
                        />
                        <div className="text-sm">کد تأیید ارسال شد، لطفاً وارد کنید.</div>
                    </div>
                ),
                { duration: 120000, id: "otp-toast", position: "top-right" }
            );
            setTimeout(() => setStep(2), 0);
        } else {
            toast.error("خطا", { description: data.error || "مشکلی در ارسال کد رخ داد." });
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const res = await signIn("phone-otp", {
            phone,
            code,
            redirect: false,
        });

        setLoading(false);
        if (res?.error) {
            toast.error("خطا", { description: res?.error || "کد وارد شده اشتباه است یا منقضی شده." });
        } else {
            toast.success("ورود موفق", { description: "در حال هدایت به داشبورد..." });

            toast.dismiss("otp-toast");

            setTimeout(() => {
                router.push("/dashboard");
            }, 500);
        }
    };

    return (
        <section className="flex flex-col items-center justify-center w-full relative">
            <div className="bg-background grid grid-cols-1 min-h-screen lg:h-screen overflow-y-hidden scrollbar-hide lg:grid-cols-2 w-full">
                <AnimationContainer reverse className="absolute top-4 md:top-8 left-4 md:left-8 z-50">
                    <div className="flex items-center">
                        <Button size="sm" variant="outline" asChild>
                            <Link href="/auth/signin">
                                برگشت
                                <ChevronLeft className="w-4 h-4 mr-1" />
                            </Link>
                        </Button>
                    </div>
                </AnimationContainer>

                <AnimationContainer delay={0.1}>
                    <div
                        style={{
                            backgroundImage: "url('/images/bg-4.jpg')",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",

                        }}
                        className="bg-muted/30 relative h-full w-full hidden lg:block">
                        <div className="flex w-4/5 mx-auto lg:px-8 items-center justify-center h-full">
                            <WrapBg />
                        </div>
                        <div className="flex flex-col items-center absolute left-1/2 bottom-[15%] -translate-x-1/2 translate-y-1/2 justify-center max-w-sm">
                            <h1 className="text-xl md:text-2xl font-semibold text-center">
                                ورود با شماره تلفن
                            </h1>
                            <p className="text-muted-foreground mt-2 text-sm md:text-base text-center">
                                شماره موبایل خود را وارد کنید تا کد تأیید دریافت کنید.
                            </p>
                        </div>
                    </div>
                </AnimationContainer>

                <AnimationContainer delay={0.2}>
                    <div className="flex mt-16 justify-center py-12 relative px-4 md:px-0">
                        <div className="mx-auto grid max-w-sm w-full gap-6">
                            <div className="flex flex-col gap-4 text-start">
                                <h1 className="text-2xl font-semibold">
                                    ورود با شماره موبایل
                                </h1>
                                <p className="text-muted-foreground">
                                    لطفاً شماره موبایل خود را وارد کنید تا کد تأیید برای شما ارسال شود.
                                </p>
                            </div>

                            {step === 1 ? (
                                <>
                                    <form onSubmit={handleSendCode} className="grid gap-4 mt-2 w-full">
                                        <div className="grid gap-2">
                                            <Label htmlFor="phone">شماره موبایل</Label>
                                            <Input
                                                required
                                                id="phone"
                                                type="tel"
                                                placeholder="مثلاً 09351234567"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                            />
                                        </div>
                                        <Button type="submit" className="w-full" disabled={loading}>
                                            {loading ? "در حال ارسال..." : "ارسال کد تأیید"}
                                        </Button>
                                    </form>
                                    <div className="mt-4 text-center text-sm">
                                        رمز عبور خود را به یاد دارید ؟{" "}
                                        <Link href='/auth/signin'>
                                            <button
                                                className="text-primaryLight"
                                                onClick={() => setStep(1)}
                                            >
                                                ورود از طریق رمز عبور
                                            </button>
                                        </Link>
                                    </div>

                                </>
                            ) : (
                                <>
                                    <form onSubmit={handleVerifyCode} className="grid gap-4 mt-2 w-full">
                                        <div className="grid gap-2" dir="ltr">
                                            <Label className="text-center" htmlFor="code">کد تأیید</Label>
                                            <OtpInput onChange={setCode} />
                                        </div>
                                        <Button type="submit" className="w-full" disabled={loading}>
                                            {loading ? "در حال بررسی..." : "تأیید کد"}
                                        </Button>
                                    </form>
                                    <div className="mt-4 text-center text-sm">
                                        شماره را اشتباه وارد کردید؟{" "}
                                        <button
                                            className="text-primaryLight"
                                            onClick={() => {
                                                setStep(1)
                                                toast.dismiss('otp-toast')
                                            }}
                                        >
                                            اصلاح شماره
                                        </button>
                                    </div>
                                </>
                            )}


                        </div>
                    </div>
                </AnimationContainer>
            </div>
        </section>
    );
};

export default VerifyPhoneSection;
